import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, X, Trash2, Edit3, Image, ExternalLink, AlertCircle, CheckCircle, Loader, Link, Plus, FileImage, ChevronLeft, ChevronRight } from 'lucide-react';

const PhotoManager = ({ activityId, token, isAdmin = false, onPhotosUpdate, schoolId, viewAllPhotos = false }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionValue, setCaptionValue] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  // Carousel modal state
  const [showCarousel, setShowCarousel] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [fullSizeUrls, setFullSizeUrls] = useState({});
  const [imageLoaded, setImageLoaded] = useState({});

  // Touch/swipe state for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const fileInputRef = useRef(null);

  const apiCall = async (url, options = {}) => {
    console.log('API Call:', url, options.method || 'GET');
    const headers = {
        'Authorization': `Bearer ${token}`,
        ...options.headers
      };

      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${url}`, {
        ...options,
        headers
      });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
        const error = await response.json();
        console.error('API Error response:', error);
        throw new Error(error.error || 'Request failed');
    }

    return response.json();
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  useEffect(() => {
    if (viewAllPhotos && schoolId) {
      fetchPhotos();
    } else if (activityId) {
      fetchPhotos();
    }
  }, [activityId, viewAllPhotos, schoolId]);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      let url;

      if (viewAllPhotos && schoolId) {
        // Fetch all photos for the school
        url = `/api/schools/${schoolId}/photos`;
      } else if (activityId && schoolId) {
        // Fetch photos for specific activity
        url = `/api/activities/${activityId}/photos?schoolId=${schoolId}`;
      } else if (activityId) {
        url = `/api/activities/${activityId}/photos`;
      } else {
        setLoading(false);
        return;
      }

      const data = await apiCall(url);
      console.log('Fetched photos:', JSON.stringify(data.photos, null, 2));
      setPhotos(data.photos);

      await fetchThumbnailUrls(data.photos);

      if (onPhotosUpdate) {
        onPhotosUpdate(data.photos.length);
      }
    } catch (error) {
      showMessage('error', 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const fetchThumbnailUrls = async (photoList) => {
    const urlPromises = photoList.map(async (photo) => {
      try {
        // For Google Photos, use the direct URL
        if (photo.googlePhotosUrl) {
          return { [photo.id]: photo.googlePhotosUrl };
        }
        
        // For S3 photos, get signed URL
        const data = await apiCall(`/api/photos/${photo.id}`);
        const thumbnailUrl = data.thumbnailSignedUrl || data.signedUrl;
        return { [photo.id]: thumbnailUrl };
      } catch (error) {
        console.error(`Failed to get signed URL for photo ${photo.id}:`, error);
        return { [photo.id]: null };
      }
    });

    const urlResults = await Promise.all(urlPromises);
    const urlMap = urlResults.reduce((acc, urlObj) => ({ ...acc, ...urlObj }), {});
    setThumbnailUrls(urlMap);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    // Check if running on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
        console.log('iOS device detected, files:', JSON.stringify(files.map(f => ({
          name: f.name,
          type: f.type,
          size: f.size,
          lastModified: f.lastModified
        })), null, 2)); // Fixed logging
    }

    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
      const maxSize = 15 * 1024 * 1024; // 15MB
      
      if (!validTypes.includes(file.type)) {
        // iOS sometimes doesn't set the correct MIME type for HEIC
        if (isIOS && (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif'))) {
          console.log('iOS HEIC file detected by extension');
          return true;
        }
        
        showMessage('error', `${file.name}: Invalid file type (${file.type}). Only JPEG, PNG, GIF, WebP, and HEIC are allowed.`);
        return false;
      }
      
      if (file.size > maxSize) {
        showMessage('error', `${file.name}: File too large. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });
    
    setSelectedFiles(validFiles);
    
    if (validFiles.length > 0) {
      setShowUpload(true);
    }
  };

  const uploadPhotos = async () => {
    if (selectedFiles.length === 0) return;

    console.log('=== STARTING UPLOAD ===');
    console.log('Selected files:', selectedFiles.map(f => ({
      name: f.name,
      type: f.type,
      size: f.size
    })));

    const formData = new FormData();
    selectedFiles.forEach((file, index) => {
      console.log(`Adding file ${index} to FormData:`, file.name);
      formData.append('photos', file);
      formData.append('caption', ''); // Individual captions can be added later
    });

    // Add schoolId to formData if provided
    if (schoolId) {
      formData.append('schoolId', schoolId);
    }

    // Log FormData contents (this is tricky, but let's try)
    console.log('FormData entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0], typeof pair[1] === 'object' ? `File: ${pair[1].name}` : pair[1]);
    }

    try {
      setLoading(true);
      console.log('Making API call to:', `/api/activities/${activityId}/photos`);

      const data = await apiCall(`/api/activities/${activityId}/photos`, {
        method: 'POST',
        body: formData  // Note: don't set Content-Type header with FormData
      });
  
      console.log('Upload response:', data);
      showMessage('success', data.message);
      setSelectedFiles([]);
      setShowUpload(false);
      await fetchPhotos();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      showMessage('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await apiCall(`/api/photos/${photoId}`, {
        method: 'DELETE'
      });
      showMessage('success', 'Photo deleted successfully');
      await fetchPhotos();
    } catch (error) {
      showMessage('error', error.message);
    }
  };

  const updateCaption = async (photoId, caption) => {
    try {
      await apiCall(`/api/photos/${photoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ caption })
      });
      
      showMessage('success', 'Caption updated successfully');
      setEditingCaption(null);
      setCaptionValue('');
      await fetchPhotos();
    } catch (error) {
      showMessage('error', error.message);
    }
  };

  const startEditCaption = (photo) => {
    setEditingCaption(photo.id);
    setCaptionValue(photo.caption || '');
  };

  const cancelEditCaption = () => {
    setEditingCaption(null);
    setCaptionValue('');
  };

  const preloadImageInBrowser = (url) => {
    const img = new Image();
    img.src = url;
  };

  const loadPhotoUrl = async (photoId) => {
    if (fullSizeUrls[photoId]) {
      return fullSizeUrls[photoId];
    }

    const photo = photos.find(p => p.id === photoId);
    if (!photo) return null;

    try {
      const data = await apiCall(`/api/photos/${photoId}`);
      const fullUrl = data.signedUrl || photo.googlePhotosUrl;
      if (fullUrl) {
        setFullSizeUrls(prev => ({ ...prev, [photoId]: fullUrl }));
        // Preload in browser cache
        preloadImageInBrowser(fullUrl);
        return fullUrl;
      }
    } catch (error) {
      console.error('Failed to load photo:', error);
    }
    return null;
  };

  const openFullImage = async (photo) => {
    const photoIndex = photos.findIndex(p => p.id === photo.id);
    setCurrentPhotoIndex(photoIndex);
    setShowCarousel(true);

    // Start loading full-size images in background (non-blocking)
    // Priority: current, then adjacent, then rest
    const nextIndex = (photoIndex + 1) % photos.length;
    const prevIndex = (photoIndex - 1 + photos.length) % photos.length;

    // Load current photo's full size
    loadPhotoUrl(photo.id);

    // Load adjacent photos
    setTimeout(() => {
      if (photos[nextIndex]) loadPhotoUrl(photos[nextIndex].id);
      if (photos[prevIndex]) loadPhotoUrl(photos[prevIndex].id);
    }, 50);

    // Load remaining photos in background with lower priority
    setTimeout(() => {
      photos.forEach(p => {
        if (p.id !== photo.id &&
            p.id !== photos[nextIndex]?.id &&
            p.id !== photos[prevIndex]?.id) {
          loadPhotoUrl(p.id);
        }
      });
    }, 500);
  };

  const closeCarousel = () => {
    setShowCarousel(false);
  };

  const goToPreviousPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => {
      const newIndex = prevIndex > 0 ? prevIndex - 1 : photos.length - 1;
      // Mark new photo as not loaded yet if we're switching to full size
      if (fullSizeUrls[photos[newIndex]?.id]) {
        setImageLoaded(prev => ({ ...prev, [photos[newIndex].id]: false }));
      }
      return newIndex;
    });
  };

  const goToNextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => {
      const newIndex = prevIndex < photos.length - 1 ? prevIndex + 1 : 0;
      // Mark new photo as not loaded yet if we're switching to full size
      if (fullSizeUrls[photos[newIndex]?.id]) {
        setImageLoaded(prev => ({ ...prev, [photos[newIndex].id]: false }));
      }
      return newIndex;
    });
  };

  const handleImageLoad = (photoId) => {
    setImageLoaded(prev => ({ ...prev, [photoId]: true }));
  };

  // Touch handlers for swipe gestures
  const minSwipeDistance = 50; // Minimum distance for a swipe

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextPhoto();
    } else if (isRightSwipe) {
      goToPreviousPhoto();
    }
  };

  // Preload adjacent photos when navigating
  useEffect(() => {
    if (!showCarousel || photos.length === 0) return;

    const nextIndex = (currentPhotoIndex + 1) % photos.length;
    const prevIndex = (currentPhotoIndex - 1 + photos.length) % photos.length;
    const nextNextIndex = (currentPhotoIndex + 2) % photos.length;
    const prevPrevIndex = (currentPhotoIndex - 2 + photos.length) % photos.length;

    // Preload adjacent photos
    [nextIndex, prevIndex, nextNextIndex, prevPrevIndex].forEach(index => {
      if (photos[index]) {
        loadPhotoUrl(photos[index].id);
      }
    });
  }, [showCarousel, currentPhotoIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!showCarousel) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPreviousPhoto();
      } else if (e.key === 'ArrowRight') {
        goToNextPhoto();
      } else if (e.key === 'Escape') {
        closeCarousel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCarousel]);

  const handleImageError = (photoId) => {
    console.log(`Image failed to load for photo ${photoId}`);
    const photo = photos.find(p => p.id === photoId);
    if (photo && !photo.googlePhotosUrl) {
      apiCall(`/api/photos/${photo.id}`)
        .then(data => {
          const thumbnailUrl = data.thumbnailSignedUrl || data.signedUrl;
          if (thumbnailUrl) {
            setThumbnailUrls(prev => ({
              ...prev,
              [photoId]: thumbnailUrl
            }));
          } else {
            setThumbnailUrls(prev => ({
              ...prev,
              [photoId]: 'failed'
            }));
          }
        })
        .catch(error => {
          console.error('Failed to refresh signed URL:', error);
          setThumbnailUrls(prev => ({
            ...prev,
            [photoId]: 'failed'
          }));
        });
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <div className="space-y-4">
        {/* Message */}
        {message.text && (
          <div className={`rounded-lg p-4 flex items-center space-x-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

      {/* Upload Section - Hide when viewing all photos */}
      {!viewAllPhotos && (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-800 flex items-center">
            <Camera className="w-5 h-5 mr-2 text-blue-500" />
            Activity Photos
          </h3>
          <button
            onClick={() => setShowUpload(true)}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>Add Photos</span>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Enhanced Upload Interface */}
        {showUpload && (
          <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-700">Upload Photos</h4>
              <button
                onClick={() => {
                  setShowUpload(false);
                  setSelectedFiles([]);
                }}
                className="text-gray-500 hover:text-red-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {selectedFiles.length === 0 ? (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400"
                >
                  <FileImage className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">Click to select photos</p>
                  <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP • Max 10MB each • Up to 5 files</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-24 object-cover rounded-lg"
                          onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                          {file.name.length > 20 ? `${file.name.substring(0, 17)}...` : file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={uploadPhotos}
                    disabled={loading}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    <span>{loading ? 'Uploading...' : 'Upload All'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Photos Grid */}
      {viewAllPhotos && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-medium text-gray-800 flex items-center">
            <Image className="w-5 h-5 mr-2 text-blue-500" />
            All Photos Across Activities
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Viewing all your photos from all activities
          </p>
        </div>
      )}

      {/* Photos Grid */}
      {loading && photos.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No photos uploaded yet</p>
          <p className="text-sm">Upload photos to document this activity</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((photo) => (
            <div key={photo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="relative">
                <img
                  src={
                    photo.googlePhotosUrl 
                      ? photo.googlePhotosUrl 
                      : (thumbnailUrls[photo.id] && thumbnailUrls[photo.id] !== 'failed')
                        ? thumbnailUrls[photo.id]
                        : '/assets/placeholder-image.png'
                  }
                  alt={photo.caption || photo.originalName}
                  className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => openFullImage(photo)}
                  onError={() => handleImageError(photo.id)}
                  loading="lazy"
                />
                
                {/* Loading indicator */}
                {!photo.googlePhotosUrl && !thumbnailUrls[photo.id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
                  </div>
                )}
                
                {/* Error state */}
                {!photo.googlePhotosUrl && thumbnailUrls[photo.id] === 'failed' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="text-center">
                      <Image className="w-8 h-8 mx-auto mb-1 text-gray-400" />
                      <p className="text-xs text-gray-500">Preview unavailable</p>
                    </div>
                  </div>
                )}
                
                {/* Overlay buttons */}
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    onClick={() => openFullImage(photo)}
                    className="bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-75"
                    title="View full size"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deletePhoto(photo.id)}
                    className="bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-75"
                    title="Delete photo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Google Photos indicator */}
                {photo.googlePhotosId && (
                  <div className="absolute bottom-2 left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs">
                    Google Photos
                  </div>
                )}
              </div>

              <div className="p-4">
                {/* Caption editing */}
                {editingCaption === photo.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={captionValue}
                      onChange={(e) => setCaptionValue(e.target.value)}
                      placeholder="Add a caption..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                      maxLength={500}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={() => updateCaption(photo.id, captionValue)}
                        className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditCaption}
                        className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {photo.caption ? (
                          <p className="text-gray-700 text-sm">{photo.caption}</p>
                        ) : (
                          <p className="text-gray-400 text-sm italic">No caption</p>
                        )}
                      </div>
                      <button
                        onClick={() => startEditCaption(photo)}
                        className="text-gray-400 hover:text-blue-500 ml-2"
                        title="Edit caption"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>Uploaded: {new Date(photo.createdAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}</p>
                      {isAdmin && photo.user && (
                        <p>By: {photo.user.firstName} {photo.user.lastName}</p>
                      )}
                      {photo.fileSize > 0 && (
                        <p>Size: {(photo.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Carousel Modal */}
      {showCarousel && photos.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close button */}
          <button
            onClick={closeCarousel}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 z-50"
            title="Close (Esc)"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Photo counter */}
          <div className="absolute top-4 left-4 text-white text-lg z-50">
            {currentPhotoIndex + 1} / {photos.length}
          </div>

          {/* Previous button */}
          {photos.length > 1 && (
            <button
              onClick={goToPreviousPhoto}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-3 bg-black bg-opacity-50 rounded-full z-50"
              title="Previous (←)"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Next button */}
          {photos.length > 1 && (
            <button
              onClick={goToNextPhoto}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 p-3 bg-black bg-opacity-50 rounded-full z-50"
              title="Next (→)"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Current photo */}
          <div className="max-w-7xl max-h-screen p-4 flex flex-col items-center justify-center w-full">
            {(thumbnailUrls[photos[currentPhotoIndex]?.id] || photos[currentPhotoIndex]?.googlePhotosUrl) ? (
              <>
                <div className="relative">
                  {/* Loading spinner overlay */}
                  {!imageLoaded[photos[currentPhotoIndex].id] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-10">
                      <Loader className="w-12 h-12 text-white animate-spin" />
                    </div>
                  )}

                  <img
                    key={photos[currentPhotoIndex].id}
                    src={
                      fullSizeUrls[photos[currentPhotoIndex].id] ||
                      photos[currentPhotoIndex].googlePhotosUrl ||
                      thumbnailUrls[photos[currentPhotoIndex].id]
                    }
                    alt={photos[currentPhotoIndex].caption || photos[currentPhotoIndex].originalName}
                    className={`max-w-full max-h-[70vh] object-contain transition-opacity duration-300 ease-in-out ${
                      imageLoaded[photos[currentPhotoIndex].id] ? 'opacity-100' : 'opacity-50'
                    }`}
                    onLoad={() => handleImageLoad(photos[currentPhotoIndex].id)}
                    style={{ animation: imageLoaded[photos[currentPhotoIndex].id] ? 'fadeIn 0.3s ease-in-out' : 'none' }}
                  />
                </div>
                {/* Caption and Details */}
                <div className="mt-4 text-white text-center max-w-3xl space-y-2">
                  {/* Description */}
                  {photos[currentPhotoIndex].caption && (
                    <p className="text-lg font-medium">{photos[currentPhotoIndex].caption}</p>
                  )}

                  {/* Activity and Date Info */}
                  <div className="text-gray-300 text-sm space-y-1">
                    {photos[currentPhotoIndex].activity && (
                      <p>
                        <span className="font-semibold">Activity:</span> {photos[currentPhotoIndex].activity.name}
                      </p>
                    )}
                    {photos[currentPhotoIndex].activity?.activeFrom && (
                      <p>
                        <span className="font-semibold">Date:</span>{' '}
                        {new Date(photos[currentPhotoIndex].activity.activeFrom).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    )}
                    {photos[currentPhotoIndex].user && (
                      <p>
                        <span className="font-semibold">Uploaded by:</span>{' '}
                        {photos[currentPhotoIndex].user.firstName} {photos[currentPhotoIndex].user.lastName}
                      </p>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="mt-2 text-gray-500 text-xs">
                    {photos[currentPhotoIndex].originalName}
                    {photos[currentPhotoIndex].googlePhotosId && (
                      <span className="ml-2 text-blue-400">• Google Photos</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-96">
                <div className="text-white text-center">
                  <Loader className="w-12 h-12 animate-spin mx-auto mb-4" />
                  <p>Loading photo...</p>
                </div>
              </div>
            )}
          </div>

          {/* Thumbnail strip at bottom (optional) */}
          {photos.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 p-4 overflow-x-auto">
              <div className="flex space-x-2 justify-center">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`flex-shrink-0 ${
                      index === currentPhotoIndex
                        ? 'ring-4 ring-blue-500'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={
                        photo.googlePhotosUrl ||
                        thumbnailUrls[photo.id] ||
                        '/assets/placeholder-image.png'
                      }
                      alt={photo.caption || photo.originalName}
                      className="w-16 h-16 object-cover rounded"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default PhotoManager;